import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../lib/designSystem/HeaderWithActions';
import CenteredCircularProgress from '../../../../../lib/designSystem/CircularProgress';
import EnhancedTable from '../../../../../lib/designSystem/Table';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import {
  validatePermissions,
  showAlert,
} from '../../../../../lib/functions/utils';
import { AuthState } from '../../../../../services/redux/authSlice';
import {
  getBusinessFiles,
  uploadBusinessFile,
  deleteBusinessFile,
  createBusinessFolder,
  downloadBusinessFile,
} from '../../../../../lib/api/business';
import UploadDialog from './components/UploadDialog';
import DeleteDialog from './components/DeleteDialog';
import CreateFolderDialog from './components/CreateFolderDialog';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const Files = (props: any) => {
  const { businessIdentifier } = props;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [miniLoading, setMiniLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [openUploadFile, setOpenUploadFile] = useState(false);
  const [file, setFile] = useState<any>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [fileToDelete, setFileToDelete] = useState<any>(null);
  const [files, setFiles] = useState<any>([]);
  const [currentPath, setCurrentPath] = useState<string>('/');
  const [openCreateFolder, setOpenCreateFolder] = useState(false);
  const [folderName, setFolderName] = useState<string>('');
  const [toDeleteFolder, setToDeleteFolder] = useState<boolean>(false);

  const setUp = async () => {
    setLoading(true);
    try {
      const response = await getBusinessFiles(dispatch, businessIdentifier, {
        relativePath: '/',
      });
      setFiles(response);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const handleChangePath = async (path: string) => {
    setLoading(true);
    try {
      let newPath;
      if (path === '/') {
        newPath = '/';
      } else if (path.startsWith('/')) {
        newPath = path;
      } else {
        newPath = currentPath === '/' ? `/${path}` : `${currentPath}${path}/`;
      }
      if (!newPath.endsWith('/')) {
        newPath = `${newPath}/`;
      }
      setCurrentPath(newPath);
      const response = await getBusinessFiles(dispatch, businessIdentifier, {
        relativePath: newPath,
      });
      setFiles(response);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const handleUploadFile = async () => {
    setLoading(true);
    setOpenUploadFile(false);
    try {
      const formData = new FormData();
      const fileToUpload = new File([file], `${uploadedFileName}`, {
        type: file.type,
      });
      formData.append('file', fileToUpload);
      formData.append('relativePath', currentPath);
      await uploadBusinessFile(dispatch, businessIdentifier, formData);
      showAlert(dispatch, 'success', 'Documento subido con éxito');
      setFile(null);
      setUploadedFileName(null);
      await handleChangePath(currentPath);
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Ha ocurrido un error al subir el documento',
      );
    }
    setLoading(false);
  };

  const handleDownloadFile = async (id: any) => {
    const fileToDownload = files.find((file: any) => file.id === id);
    if (!fileToDownload) return;

    const type = fileToDownload.type;
    if (type === 'carpeta') {
      return await handleChangePath(`${currentPath}${fileToDownload.name}`);
    }
    if (!validatePermissions(user, 'business_can_download_files')) {
      showAlert(
        dispatch,
        'error',
        'No tienes permisos para descargar documentos',
      );
      return;
    }
    setMiniLoading(true);
    try {
      const response = await downloadBusinessFile(
        dispatch,
        businessIdentifier,
        {
          relativePath: currentPath,
          fileName: fileToDownload.name,
        },
      );
      const url = response.signedUrl;
      window.open(url, '_blank');
      showAlert(dispatch, 'success', 'Documento descargado con éxito');
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Ha ocurrido un error al descargar el documento',
      );
    } finally {
      setMiniLoading(false);
    }
  };

  const handleDeleteFile = (id: any) => {
    const fileToDel = files.find((file: any) => file.id === id);
    if (!fileToDel) return;
    setFileToDelete(fileToDel);
    if (fileToDel.type === 'carpeta') {
      setToDeleteFolder(true);
    }
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      await deleteBusinessFile(dispatch, businessIdentifier, {
        relativePath: currentPath,
        fileName: fileToDelete.name,
      });
      showAlert(dispatch, 'success', 'Documento eliminado con éxito');
      setFileToDelete(null);
      setConfirmDeleteOpen(false);
      setToDeleteFolder(false);
      await handleChangePath(currentPath);
    } catch (error) {
      showAlert(
        dispatch,
        'error',
        'Ha ocurrido un error al eliminar el documento',
      );
    }
    setLoading(false);
  };

  const handleFileChange = (event: any) => {
    const uploadedFile = event.target.files[0];
    setUploadedFileName(uploadedFile.name);
    setFile(uploadedFile);
  };

  const handleCreateFolder = async () => {
    setLoading(true);
    try {
      await createBusinessFolder(dispatch, businessIdentifier, {
        relativePath: currentPath,
        folderName: folderName,
      });
      showAlert(dispatch, 'success', 'Carpeta creada con éxito');
      setFolderName('');
      setOpenCreateFolder(false);
      await handleChangePath(currentPath);
    } catch (error) {
      showAlert(dispatch, 'error', 'Ha ocurrido un error al crear la carpeta');
    }
    setLoading(false);
  };

  useEffect(() => {
    setUp();
  }, []);

  const actions = [
    {
      action: handleDeleteFile,
      actionLabel: 'Eliminar',
      disabled: !validatePermissions(user, 'business_can_manage_files'),
    },
  ];

  const buttons: CustomButtonProps[] = [
    {
      name: 'Crear carpeta',
      action: () => setOpenCreateFolder(true),
      color: 'secondary',
      disabled: !validatePermissions(user, 'business_can_create_folders'),
    },
    {
      name: 'Subir archivo',
      action: () => setOpenUploadFile(true),
      color: 'primary',
      disabled: !validatePermissions(user, 'business_can_upload_files'),
    },
  ];

  if (loading) return <CenteredCircularProgress relative />;

  return (
    <div>
      <HeaderWithActions
        customTitle={
          <Breadcrumbs
            color="textPrimary"
            style={{ marginLeft: '10px' }}
            separator={<NavigateNextIcon fontSize="small" />}
          >
            <Link
              onClick={() => handleChangePath('/')}
              underline="hover"
              sx={{
                '&:hover': {
                  textDecorationColor:
                    currentPath === '/' ? 'primary' : 'black',
                },
              }}
            >
              <Typography
                color={currentPath === '/' ? 'primary' : 'textPrimary'}
                fontWeight="bold"
              >
                Home
              </Typography>
            </Link>
            {currentPath
              .split('/')
              .filter((segment) => segment) // Filtrar cadenas vacías
              .reduce<string[]>((acc, segment) => {
                const lastPath = acc.length ? acc[acc.length - 1] : '/';
                const newPath =
                  lastPath === '/' ? `/${segment}` : `${lastPath}/${segment}`;
                acc.push(newPath);
                return acc;
              }, [])
              .map((path, index, arr) => {
                const isLast = index === arr.length - 1; // Check if it's the last item
                return (
                  <Link
                    key={path}
                    onClick={() => handleChangePath(path)}
                    underline="hover"
                    sx={{
                      '&:hover': {
                        textDecorationColor: isLast ? 'primary' : 'black',
                      },
                    }}
                  >
                    <Typography
                      color={isLast ? 'primary' : 'textPrimary'}
                      fontWeight="bold"
                    >
                      {path.split('/').pop() || 'Home'}
                    </Typography>
                  </Link>
                );
              })}
          </Breadcrumbs>
        }
        buttons={buttons}
      />
      {miniLoading && <CenteredCircularProgress />}
      <EnhancedTable
        dataHeaders={[
          {
            key: 'name',
            label: 'Nombre',
            handleAction: handleDownloadFile,
            type: 'folder-system',
          },
          { key: 'type', label: 'Tipo' },
          { key: 'size', label: 'Tamaño' },
          {
            key: 'lastModified',
            label: 'Última modificación',
            type: 'date-large',
          },
        ]}
        data={files}
        actions={actions}
        showActions
        rowsPerPageDefault={100}
      />
      <UploadDialog
        openUploadFile={openUploadFile}
        setOpenUploadFile={setOpenUploadFile}
        handleFileChange={handleFileChange}
        uploadedFileName={uploadedFileName}
        setUploadedFileName={setUploadedFileName}
        handleUploadFile={handleUploadFile}
      />
      <DeleteDialog
        confirmDeleteOpen={confirmDeleteOpen}
        setConfirmDeleteOpen={setConfirmDeleteOpen}
        handleConfirmDelete={handleConfirmDelete}
        toDeleteFolder={toDeleteFolder}
        setToDeleteFolder={setToDeleteFolder}
        fileToDelete={fileToDelete}
      />
      <CreateFolderDialog
        openCreateFolder={openCreateFolder}
        setOpenCreateFolder={setOpenCreateFolder}
        handleCreateFolder={handleCreateFolder}
        folderName={folderName}
        setFolderName={setFolderName}
      />
    </div>
  );
};

export default Files;
